import type { GetServerSideProps } from "next";
import { UAParser } from "ua-parser-js";

//Components
import { Ribbon } from "@/components/landing/Ribbon";
import { Hero } from "@/components/landing/Hero";
import { Terms } from "@/components/landing/Terms";
import { Cake } from "@/components/landing/Cake/index";
import { LowerContentBlock } from "@/components/common/LowerContentBlock";
import { USPAttributesIconGroup } from "@/components/common/USPAttributesIconGroup";
import OffersPage from "../index";
import { CompetitionPage } from "@/components/competitions/CompetitionPage";

//API
import { getLandingPage } from "@/api/landing";
import { getOfferPage } from "@/api/offers";
import { getGlobalConfig } from "@/api/globalConfig";
import { visitReport } from "@/api/report/visit";
import { isLandingPageCompleted } from "@/api/report/index";
import { getSitePopulateQuery } from "@/api/sites";
import { sanitiseGeoProfile } from "@/api/geoProfile";
import { getCompetitionPage } from "@/api/competitions/competitions";

// Types
import type {
    LandingPageTemplateResponse,
    LandingOrOfferPageComponent,
} from "@/types/landing";
import type { PageProps, SessionCookie, UserCookie } from "@/types/nextjs";
import type {
    OfferPageTemplateResponse,
    SubIdProps,
} from "@/types/offers";
import type { GeoProfileResponse } from "@/types/geoProfile";
import type { CompetitionPageTemplate } from "@/types/competitions";

//Utils
import { makeLandingPageTheme, makeOfferPageTheme } from "src/utils/theme";
import { getQueryParam } from "src/utils/getPageAndSite";
import { getPageUSPAttributes } from "src/utils/utils";
import {
    getAppProps,
    getPageThemeAndUpdateCookies,
} from "../../utils/globalServerData";
import {
    getDefaultOfferPageFromDefaultCampaign,
    getOfferClaimCounts,
} from "../../utils/serverOfferPageSetupUtils";
import { validateOfferPage } from "../../utils/clientOfferPageSetupUtils";

// Cookie Utils

import {
    getCookieObject,
    getBypassLeadFormServerSide,
    updateSessionCookie,
    getServerCookie,
    setCookie,
} from "src/utils/cookies/server";
import { generateRandomCookieValue } from "src/utils/cookies/shared";

// Constants
import {
    SESSION_COOKIE_NAME,
    USER_COOKIE_NAME,
    LANDING_PAGE_TEMPLATE_CACHE_KEY_COOKIE_NAME,
    LANDING_PAGE_COMPLETE_COOKIE_NAME,
    COMPLETED_COMPETITION_DRAW_ID_COOKIE
} from "src/constants";
import { exportServerTimings } from "@/utils/serverTiming";
import { startServerTimeLog } from "@/logging/logger";
import { makeCompetitionPageTheme } from "@/utils/theme/makeTheme";
import { generatePageContext } from "@/contexts/PageContext/lib/generator";
import { getCampaign, getCompetitionPageFromSlug, getDefaultLandingPageFromSite, getLandingPageId, getOfferPageFromCategory, getOfferPageFromSlug, getOfferPageId, getOfferSlugFromLandingPage, validateCompetitionPage, validateLandingPage } from "@/lib/page";

const Home: LandingOrOfferPageComponent = ({
    site,
    cakeRequestIdFromCookie,
    bypassLeadFormServerSide,
    initialViewportWidth,
    pageData,
}) => {
    const { offerPage, landingPage, pageUSPAttributes, auj, page } = pageData;
    const {
        sanitisedLowerContentBlockText = "",
        sanitisedTermsAndConditionsText = "",
        sanitisedRibbonText = "",
    } = landingPage?.data?.attributes ?? {};

    if (offerPage?.data != null) {
        return (
            <OffersPage
                pageData={pageData}
                site={site}
                bypassLeadFormServerSide={bypassLeadFormServerSide || false}
                initialViewportWidth={initialViewportWidth}
            />
        );
    }

    if (pageData.landingPage?.data != null) {
        return (
            <div>
                <main>
                    <Hero site={site} landingPage={pageData.landingPage} />
                    {pageData.pageUSPAttributes && (
                        <USPAttributesIconGroup
                            pageUSPAttributes={pageData.pageUSPAttributes}
                        />
                    )}
                    {sanitisedLowerContentBlockText.trim() != "" && (
                        <LowerContentBlock
                            lowerContentBlockText={
                                sanitisedLowerContentBlockText
                            }
                        />
                    )}
                    <Terms
                        termsAndConditionsText={sanitisedTermsAndConditionsText}
                    />
                </main>
                <Ribbon ribbonText={sanitisedRibbonText} />
                <Cake
                    cakeClickPixelUrl={site?.attributes?.cakeClickPixelUrl}
                    cakeRequestIdFromCookie={cakeRequestIdFromCookie}
                />
            </div>
        );
    }

    if (page && page.pageType === "competition") {
        return (
            <CompetitionPage
                competitionPage={page.page as CompetitionPageTemplate}
                site={site}
                initialViewportWidth={initialViewportWidth}
            />
        );
    }

    return null;
};

export const getServerSideProps: GetServerSideProps<PageProps> = async (
    context
) => {
    const { endTimeLog } = startServerTimeLog("getServerSideProps", {
        page: "withSlug",
    });

    const slugId = getQueryParam(context.query, "id");
    const queryString = context.query;
    // Anything that NextJS can't find, it will route to this page
    // (nobody tell JG, he already hates NextJS enough)
    // This interferes with the reporting as it creates visit reports with
    // the landing_url as the page that doesn't exist
    // It also overburdens the server with requests for things that don't exist

    const ignoredPaths = [
        "redirect",
        // We have no service worker, so we don't want to be looking for it
        // If this requests comes here, ignore it
        "service-worker.js",
        // I don't really know when or why these requests are coming here,
        // but it has been observed in prod. If it gets here, it's lost.
        // There is some more information about these requests on this page:
        // https://github.com/vercel/next.js/discussions/41047#discussioncomment-3769637
        "_next/static",
        // Could be resolved by putting a favicon in the public folder, but our favicons are dynamic
        // https://github.com/vercel/next.js/discussions/13301
        "favicon.ico",
        // When the page first loads, we have placeholders for the merchant logos
        // These placeholders are quickly replaced, but not before NextJS tries to find them
        // By not including the closing brace, we can catch the two variations,
        // merchantLogo and merchantLogoSecondary
        "${merchantLogo",
        "$%7BmerchantLogo",
    ];
    if (
        context.req.url?.startsWith("/redirect") ||
        context.req.url?.startsWith("/api")
    ) {
        return {
            notFound: true,
        };
    }
    if (ignoredPaths.some((path) => slugId?.includes(path))) {
        return {
            notFound: true,
        };
    }

    const visitId = generateRandomCookieValue();
    const siteWithDefaultsAndCampaignsPromise = getAppProps({
        context,
        sitePopulateQuery: getSitePopulateQuery(true),
        visitId,
    });

    const { site, siteTheme, managedCookiesPromise } =
        await siteWithDefaultsAndCampaignsPromise;

    const { categoryOfferPage, redirectDefault } = getOfferPageFromCategory(
        site,
        context.query
    );

    if (redirectDefault) {
        return {
            redirect: {
                destination: redirectDefault,
                permanent: false,
            },
        };
    }

    const offerSlugPage = getOfferPageFromSlug(
        site?.attributes?.offer_page_templates?.data,
        slugId
    );

    const competitionSlugPage = getCompetitionPageFromSlug(
        site?.attributes?.competitions,
        slugId
    );

    const campaignInUse = getCampaign(slugId, site);

    const landingPagePreviewId = parseInt(
        getQueryParam(context.query, "previewId")
    );
    const desiredOfferPageId = getOfferPageId(campaignInUse);
    const desiredLandingPageId = getLandingPageId(campaignInUse);

    const cookies = await managedCookiesPromise;
    const subIdProps: SubIdProps = {
        userCookie: cookies[USER_COOKIE_NAME],
        sessionCookie: cookies[SESSION_COOKIE_NAME],
        shortCode: site?.attributes?.shortCode ?? "",
    };

    let landingPagePromise = null;
    let offerPagePromise = null;
    let competitionPagePromise = null;

    // Choose which page to load in the following order
    // 1. A preview ID for a landing page should always be shown
    // 2. The offer page assigned to the campaign (if there is one)
    // 3. The landing page assigned to the campaign (if there is one)
    // 4. The offer page on the default campaign on the site (if there is one)
    // 5. The landing page on the default campaign on the site (if there is one)

    if (landingPagePreviewId) {
        landingPagePromise = getLandingPage(
            landingPagePreviewId,
            queryString,
            // context.query was added as part of ACD-13073
            // when it was believed that we would need to set "publicationState: preview"
            // for the preview to work. Without this, "get entries" would only return
            // published pages. However, it turns out that we don't need to set this
            // because we are using the "get entry by ID" endpoint, which returns
            // unpublished content as well.
            // With this line in place, we are not caching to our full potential
            // because query parameters are placed into the request to strapi
            // so /lp100?var7=1 and /lp100?var7=2 are treated as different requests,
            // when the same content is returned.

            // context.query
        );
    } else if (categoryOfferPage) {
        offerPagePromise = getOfferPage(
            categoryOfferPage.id,
            subIdProps
        );
    } else if (desiredOfferPageId) {
        offerPagePromise = getOfferPage(
            desiredOfferPageId,
            subIdProps
            // context.query
        );
    } else if (desiredLandingPageId) {
        landingPagePromise = getLandingPage(
            desiredLandingPageId,
            queryString
            // context.query
        );
    } else if (offerSlugPage) {
        offerPagePromise = getOfferPage(
            offerSlugPage.id,
            subIdProps,
        );
    } else if (competitionSlugPage) {
        competitionPagePromise = getCompetitionPage(
            competitionSlugPage,
            subIdProps
        );
    }

    let offerPage: OfferPageTemplateResponse | null = null;
    let landingPage: LandingPageTemplateResponse | null = null;
    let landingPageCacheKey: string | null = null;
    let competitionPage: CompetitionPageTemplate | null = null;

    if (offerPagePromise) {
        offerPage = await offerPagePromise;
    } else if (landingPagePromise) {
        const getLandingPageResult = await landingPagePromise;
        landingPage = getLandingPageResult?.sanitisedLandingPage ?? null;
        landingPageCacheKey = getLandingPageResult?.cacheKey ?? null;
    } else if (competitionPagePromise) {
        competitionPage = await competitionPagePromise;
        
        // Get existing completed competitions
        const competitionCookie = getServerCookie(
            context,
            COMPLETED_COMPETITION_DRAW_ID_COOKIE
        );

        let completedCompetitions = [];
        if (competitionCookie && typeof competitionCookie === 'string' && competitionCookie.length < 1000) {
            try {
                completedCompetitions = JSON.parse(competitionCookie as string);
            } catch (e) {
                completedCompetitions = [];
            }
        }
        
        // Check if the current competition has been completed
        if (competitionPage && 
            competitionPage.draw_id && 
            competitionPage.list_id && 
            competitionPage.offer_page_template_url) {
            const competitionId = `${competitionPage.draw_id}_${competitionPage.list_id}`;
            if (Array.isArray(completedCompetitions) && 
                completedCompetitions.includes(competitionId)) {
                const fromPath = Array.isArray(context.query.id) 
                    ? context.query.id.join('/') 
                    : context.query.id || '';
                return {
                    redirect: {
                        // react will automaticlaly at 'id' but to match anchoris implementation we need 'from'
                        destination: competitionPage.offer_page_template_url + 
                            '?from=' + fromPath +
                            (Object.keys(context.query).length > 1  // > 1 because 'id' is always present
                                ? '&' + new URLSearchParams(
                                    Object.fromEntries(
                                        Object.entries(context.query).filter(([key]) => key !== 'id')
                                    ) as Record<string, string>
                                ).toString() 
                                : ''),
                        permanent: false,
                    },
                };
            }
        }
    }

    let validOfferPage = validateOfferPage(offerPage);
    let validLandingPage = validateLandingPage(landingPage);
    const validCompetitionPage = validateCompetitionPage(competitionPage);

    // If we have no pages from the desired IDs, try the default IDs
    if (!validOfferPage && !validLandingPage && !validCompetitionPage) {
        const defaultOfferPageId = getDefaultOfferPageFromDefaultCampaign(site);
        if (defaultOfferPageId) {
            offerPage = await getOfferPage(
                defaultOfferPageId,
                subIdProps
                // context.query
            );
            validOfferPage = validateOfferPage(offerPage);
        }
        if (!validOfferPage) {
            const defaultLandingPageId = getDefaultLandingPageFromSite(site);
            if (defaultLandingPageId) {
                const getLandingPageResult = await getLandingPage(
                    defaultLandingPageId,
                    queryString,
                    // context.query
                );
                landingPage =
                    getLandingPageResult?.sanitisedLandingPage || null;
                landingPageCacheKey = getLandingPageResult?.cacheKey ?? null;
                validLandingPage = validateLandingPage(landingPage);
            }
        }
    }

    let pageThemePromise: Promise<string>;
    if (offerPage) {
        pageThemePromise = makeOfferPageTheme(offerPage);
        offerPage.offersClaimed = getOfferClaimCounts(context, site);
        await sanitiseGeoProfile(
            offerPage?.data?.attributes?.geo_profile,
            site
        );
    } else if (competitionPage) {
        pageThemePromise = makeCompetitionPageTheme(competitionPage);
        await sanitiseGeoProfile(
            competitionPage.geo_profile,
            site
        );
    } else {
        pageThemePromise = makeLandingPageTheme(landingPage);
        await sanitiseGeoProfile(
            landingPage?.data?.attributes?.geo_profile,
            site
        );
    }

    visitReport({
        context,
        visitId,
        site,
        landingPage: landingPage ?? null,
        offerPage: offerPage ?? null,
    });

    // from context - test if this is correct if we use the updated cookie object
    const userCookie = getCookieObject<UserCookie>(context, USER_COOKIE_NAME);
    const sessionCookie = getCookieObject<SessionCookie>(
        context,
        SESSION_COOKIE_NAME
    );

    const landingPageToReturn = validLandingPage ? landingPage : null;
    const offerPageToReturn = validOfferPage ? offerPage : null;
    const competitionPageToReturn = validCompetitionPage ? competitionPage : null;
    
    const landingPageCompleteClientSide = getServerCookie(
        context,
        LANDING_PAGE_COMPLETE_COOKIE_NAME
    );

    const bypassLeadFormServerSide = getBypassLeadFormServerSide(
        userCookie,
        sessionCookie,
        landingPageCompleteClientSide === true,
        landingPageToReturn ||
            offerPageToReturn?.data?.attributes?.locked_landing_page
    );

    if (bypassLeadFormServerSide && !validOfferPage) {
        const offerSlug = getOfferSlugFromLandingPage(
            site?.attributes?.offer_page_templates?.data,
            landingPageToReturn?.data?.attributes?.offer_page_id
        );

        endTimeLog(false, {
            isRedirect: true,
        });
        return {
            redirect: {
                destination: `/${offerSlug}?from=${context.resolvedUrl ?? ""}`,
                permanent: false,
            },
        };
    } else {
        const pageTheme = await getPageThemeAndUpdateCookies<SessionCookie>(
            pageThemePromise,
            updateSessionCookie,
            context,
            getGlobalConfig(),
            (currentValue) => {
                return {
                    cmsCampaignId: slugId,
                    cmsLandingPageId: String(landingPage?.data?.id ?? ""),
                    completed: isLandingPageCompleted(context, currentValue),
                };
            }
        );

        const pageUSPAttributes = getPageUSPAttributes(
            site,
            landingPage,
            offerPage
        );

        // If we have a landing page template then we need this data in order to work
        // out the offer slug that the user should be redirected to.
        // If we're already on an offer page then we don't need this data.
        if (!validLandingPage && validOfferPage) {
            delete site?.attributes.offer_page_templates;
            delete site?.attributes?.campaigns;
        }

        if (landingPageCacheKey) {
            // Set cookie server-side
            setCookie(
                context,
                LANDING_PAGE_TEMPLATE_CACHE_KEY_COOKIE_NAME,
                `${landingPageCacheKey}; Path=/`,
                getGlobalConfig().sessionCookieMaxAgeMinutes
            );
        }

        const ua = context.req.headers["user-agent"];
        const parser = new UAParser(ua);
        const deviceType = parser.getDevice().type; 

        const auj = landingPageToReturn?.data?.attributes?.sanitisedAUJ?.templateVars.internalName || null;
        
        let initialViewportWidth = 1920;
        if (deviceType === "mobile") {
            initialViewportWidth = 375;
        } else if (deviceType === "tablet") {
            initialViewportWidth = 768;
        }
        const page = site && await generatePageContext(context, site, cookies) || null;
        context.res.setHeader('Server-Timing', exportServerTimings());
        const pageData = {
            page,
            pageTheme,
            landingPage: landingPageToReturn || null,
            offerPage: offerPageToReturn || null,
            pageUSPAttributes,
            auj
        }   
        endTimeLog(false, { isRedirect: false });
        return {
            props: {
                site,
                siteTheme,
                cakeRequestIdFromCookie: parseInt(
                    sessionCookie?.utm_click_id ?? ""
                ),
                bypassLeadFormServerSide: bypassLeadFormServerSide || false,
                initialViewportWidth,
                pageData
            },
            notFound: !(site && (validLandingPage || validOfferPage || validCompetitionPage)),
        };
    }
};

export default Home;
